import React, { useEffect, useMemo } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FullScreen from "@mui/icons-material/Fullscreen";
import FullScreenExit from "@mui/icons-material/FullscreenExit";
import { IconButton, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useFormatNumericHook } from "hooks";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { useReportsActions, useReportsQueriesSelector } from "store";
import { ClientDealTableRow } from "store/types";
import { formatNumber } from "utils";

interface Props {
  dealId: number;
}

export const DealHedgeFundTable: React.FC<Props> = ({ dealId }) => {
  const { getHedgeFundTableActon } = useReportsActions();
  const { use_accounting_date } = useReportsQueriesSelector();
  const { action, data, loading } = useActionHook(getHedgeFundTableActon);
  const formatNumeric = useFormatNumericHook();

  useEffect(() => {
    action({ filters: { deal_id: [dealId] }, groupBy: [], use_accounting_date, rowBy: "year" });
  }, [dealId]);

  const mrtColumns = useMemo<MRT_ColumnDef<ClientDealTableRow>[]>(() => {
    if (!data) {
      return [];
    }

    const columns: MRT_ColumnDef<ClientDealTableRow>[] = data.columns.map<MRT_ColumnDef<ClientDealTableRow>>((col) => {
      return {
        header: col.title,
        id: col.key,
        accessorKey: col.key,
        sortingFn: "basic",
        accessorFn: (row) => {
          const raw = row.valueData[col.key] as unknown as (number | null)[] | number | null;
          if (Array.isArray(raw)) return raw[0] ?? null;
          return raw;
        },
        Cell: ({ row }) => {
          const raw = row.original.valueData[col.key] as unknown as (number | null)[] | number | null;
          const postfix = col.postfix || row.original.postfix || row.original.postfixes?.[col.key] || "";
          const format = (v: number | null) => (v != null ? formatNumber(v) + postfix : "-");
          const formatPNL = (v: number | null) => {
            if (v == null) return "-";
            return formatNumeric({ value: v, format: { abbreviated: true, decimal: 2, accountingNegative: true } });
          };

          if (!Array.isArray(raw)) {
            return <span>{format(raw as number | null)}</span>;
          }

          const [monthly, pnl, ttm] = raw;
          const valueColor = (v: number | null) => v == null || v === 0 ? "inherit" : v > 0 ? "success.main" : "error.main";
          return (
            <Stack
              alignItems="center"
              spacing={0.25}
              sx={{
                "& .hf-label": { display: "inline-block", maxWidth: 0, overflow: "hidden", opacity: 0, whiteSpace: "nowrap", transition: "max-width 0.2s ease, opacity 0.2s ease" },
                "&:hover .hf-label": { maxWidth: "40px", opacity: 1 }
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 600, lineHeight: 1.3, color: valueColor(monthly) }}>
                {format(monthly)}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: valueColor(pnl), lineHeight: 1.2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <span className="hf-label">P&amp;L:&nbsp;</span>{formatPNL(pnl)}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: valueColor(ttm), lineHeight: 1.2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <span className="hf-label">TTM:&nbsp;</span>{format(ttm)}
              </Typography>
            </Stack>
          );
        },
        size: 100
      };
    });

    columns.unshift({
      header: "Year",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false
    } as MRT_ColumnDef<ClientDealTableRow>);
    return columns;
  }, [data, formatNumeric]);

  return (
    <MaterialReactTable
      key={(!!data).toString()} // That recalculates the initial state once the data is loaded
      columns={mrtColumns}
      data={data?.rows || []}
      getRowId={(row) => row.id!}
      filterFromLeafRows
      state={{ isLoading: loading }}
      enableColumnActions={false}
      enablePagination={false}
      enableDensityToggle={false}
      enableStickyHeader
      layoutMode="grid"
      enableBottomToolbar={false}
      renderTopToolbar={({ table }) => (
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "22px"
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071F35", lineHeight: "24px" }}>
            Fund Performance
          </Typography>
          <Stack>
            <MaterialTooltip isShow title="Toggle Expand" placement="top">
              <IconButton onClick={() => table.setIsFullScreen(!table.getState().isFullScreen)}>
                {table.getState().isFullScreen ? <FullScreenExit /> : <FullScreen />}
              </IconButton>
            </MaterialTooltip>
          </Stack>
        </Stack>
      )}
      muiTablePaperProps={({ table }) => ({
        sx: {
          boxShadow: "sm",
          padding: table.getState().isFullScreen ? "10px !important" : "22px",
          ...(!table.getState().isFullScreen && { borderRadius: "16px" }),
          bgcolor: "white",
          flexShrink: 0,
          "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
            "&:first-child": {
              overflow: "visible !important"
            }
          }
        }
      })}
      muiTableContainerProps={({ table }) => ({
        sx: {
          ...(!table.getState().isFullScreen && { borderRadius: "8px" }),
          height: "calc(100% - 52px)",
          maxHeight: "clamp(350px, calc(100vh - 400px), 9999px)",
          border: "1px solid #DBE8F5",
          minHeight: 250
        }
      })}
      muiTableHeadRowProps={{ sx: { backgroundColor: "#F6F8FC" } }}
      muiTableHeadCellProps={{
        align: "center",
        sx: { borderColor: "gray.50", p: "12px 8px", ".MuiDivider-root": { borderWidth: 1 }, fontWeight: 500 }
      }}
      muiTableBodyRowProps={{ sx: { backgroundColor: "white" } }}
      muiTableBodyCellProps={{
        align: "center",
        sx: {
          borderColor: "gray.50",
          p: "8px 8px",
          // "&:nth-child(2)": { fontWeight: 700 },
          ":first-child": { fontWeight: 700, borderRight: "1px solid #DBE8F5" },
          ":last-child": { fontWeight: 700, borderLeft: "1px solid #DBE8F5" }
        }
      }}
      icons={{
        ArrowDownwardIcon: (props: { className: string }) => (
          <div className="measures-table__sort-icons">
            <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
            <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
          </div>
        ),
        DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
      }}
      // muiExpandButtonProps={({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } })}
      // muiExpandAllButtonProps={{ color: "primary" }}
    />
  );
};
